import { useSelector } from 'react-redux';

import { ReuseTimerNodeDialog } from '@/components/organisms/Dialogs/Action/ReuseTimerNodeDialog/ReuseTimerNodeDialog';
import { ModalIntegrationApiKeyCreated } from '@/components/organisms/Integrations/Custom/components/ModalIntegrationApiKeyCreated';
import { ModalAddCard } from '@/modules/billing/components/ModalAddCard/ModalAddCard';
import ModalUpgradePlan from '@/modules/billing/components/ModalUpgradePlan/ModalUpgradePlan';
import ModalBundleCreation from '@/modules/bundles/components/ModalBundleCreation';
import ModalBundleDelete from '@/modules/bundles/components/ModalBundleDelete';
import ModalMergeConflicts from '@/modules/bundles/components/ModalMergeConflicts/ModalMergeConflicts';
import { ModalDepartmentCreate } from '@/modules/departments/components/ModalDepartmentCreate/ModalDepartmentCreate';
import { NewMessageModal } from '@/modules/humanChat/components/NewMessageModal/NewMessageModal';
import { ModalTryIt } from '@/modules/tryIt/ModalTryIt';
import { ModalKind } from '@/shared/types/modals';
import { RootState } from '@/shared/types/state';

import ModalAccountTransfer from './ModalAccountTransfer';
import { ModalAIAgentCreate } from './ModalAIAgentCreate/ModalAIAgentCreate';
import ModalAutoBrain from './ModalAutoBrain/ModalAutoBrain';
import ModalBrainDelete from './ModalBrainDelete';
import ModalCannedResponses from './ModalCannedResponses/ModalCannedResponses';
import ModalConfirmation from './ModalConfirmation/ModalConfirmation';
import ModalConfirmChanges from './ModalConfirmChanges/ModalConfirmChanges';
import ModalConnectIntent from './ModalConnectIntent/ModalConnectIntent';
import { ModalCreateSampleDialog } from './ModalCreateSampleDialog';
import ModalDelete from './ModalDelete';
import ModalDeskCreation from './ModalDeskCreation';
import ModalDocumentPreview from './ModalDocumentPreview';
import ModalEdit from './ModalEdit';
import ModalEditMultipleValues from './ModalEditMultipleValues';
import ModalForm from './ModalForm';
import ModalImageCrop from './ModalImageCrop';
import ModalImport from './ModalImport';
import { ModalMediaPreview } from './ModalMediaPreview/ModalMediaPreview';
import ModalMemberInvite from './ModalMemberInvite/ModalMemberInvite';
import { ModalPreviewDialog } from './ModalPreviewDialog/ModalPreviewDialog';
import ModalPrompt from './ModalPrompt';
import ModalPublishVersion from './ModalPublishVersion/ModalPublishVersion';
import ModalRoleEdit from './ModalRoleEdit/ModalRoleEdit';
import ModalSmallScreen from './ModalSmallScreen/ModalSmallScreen';
import ModalTeam from './ModalTeam/ModalTeam';
import ModalTeamMembers from './ModalTeamMembers/ModalTeamMembers';
import { ModalTemplateMessage } from './ModalTemplateMessage/ModalTemplateMessage';
import ModalUnsavedChanges from './ModalUnsavedChanges/ModalUnsavedChanges';
import ModalVersionDelete from './ModalVersionDelete';
import { ModalViews } from './ModalViews/ModalViews';
import ModalWarn from './ModalWarn';
import ModalWhatsAppTest from './ModalWhatsAppTest/ModalWhatsAppTest';

export const DESK_CREATE = 'DESK_CREATE';
export const BRAIN_DELETE = 'BRAIN_DELETE';

export const BUNDLE_CREATE = 'BUNDLE_CREATE';
export const BUNDLE_DELETE = 'BUNDLE_DELETE';

export const VERSION_DELETE = 'VERSION_DELETE';

export const MODAL_DELETE = 'MODAL_DELETE';
export const MODAL_IMAGE_CROP = 'MODAL_IMAGE_CROP';
export const MODAL_MEMBER_INVITE = 'MODAL_MEMBER_INVITE';
export const MODAL_TRYIT = 'MODAL_TRYIT';
export const MODAL_EDIT = 'MODAL_EDIT';
export const MODAL_EDIT_MULTIPLE = 'MODAL_EDIT_MULTIPLE';
export const MODAL_FORM = 'MODAL_FORM';
export const ADD_CARD = 'ADD_CARD';
export const MODAL_CONFIRMATION = 'MODAL_CONFIRMATION';
export const MODAL_PROMPT = 'MODAL_PROMPT';
export const MODAL_CONFIRM_CHANGES = 'MODAL_CONFIRM_CHANGES';
export const MODAL_CANNED_RESPONSES = 'MODAL_CANNED_RESPONSES';
export const MODAL_ROLE_EDIT = 'MODAL_ROLE_EDIT';
export const MODAL_IMPORT = 'MODAL_IMPORT';
export const MODAL_TEAM = 'MODAL_TEAM';
export const MODAL_TEAM_MEMBERS = 'MODAL_TEAM_MEMBERS';
export const MODAL_MERGE_CONFLICTS = 'MODAL_MERGE_CONFLICTS';
export const MODAL_CONNECT_INTENT = 'MODAL_CONNECT_INTENT';
export const MODAL_AUTO_BRAIN = 'MODAL_AUTO_BRAIN';
export const MODAL_WARN = 'MODAL_WARN';
export const MODAL_WHATSAPP_TEST = 'MODAL_WHATSAPP_TEST';
export const MODAL_DEPARTMENT_CREATE = 'MODAL_DEPARTMENT_CREATE';
export const MODAL_ACCOUNT_TRANSFER = 'MODAL_ACCOUNT_TRANSFER';
export const MODAL_UPGRADE_PLAN = 'MODAL_UPGRADE_PLAN';
export const MODAL_SMALL_SCREEN = 'MODAL_SMALL_SCREEN';
export const MODAL_MEDIA_PREVIEW = 'MODAL_MEDIA_PREVIEW';
export const MODAL_AI_AGENT_CREATE = 'MODAL_AI_AGENT_CREATE';
export const MODAL_TEMPLATE_MESSAGE = 'MODAL_TEMPLATE_MESSAGE';
export const MODAL_UNSAVED_CHANGES = 'MODAL_UNSAVED_CHANGES';
export const MODAL_VIEWS = 'MODAL_VIEWS';
export const MODAL_DIALOG_PREVIEW = 'MODAL_DIALOG_PREVIEW';
export const MODAL_CREATE_SAMPLE_DIALOG = 'MODAL_CREATE_SAMPLE_DIALOG';
export const MODAL_DOCUMENT_PREVIEW = 'MODAL_DOCUMENT_PREVIEW';
export const MODAL_NEW_MESSAGE = 'MODAL_NEW_MESSAGE';
export const MODAL_PUBLISH_VERSION = 'MODAL_PUBLISH_VERSION';
export const MODAL_INTEGRATION_API_KEY_CREATED = 'MODAL_INTEGRATION_API_KEY_CREATED';
export const MODAL_REUSE_TIMER = 'MODAL_REUSE_TIMER';

/**
 * Maps modal kinds to their respective components
 */
export const MODAL_KINDS: Record<ModalKind, React.ComponentType> = {
  [MODAL_SMALL_SCREEN]: ModalSmallScreen,
  [ADD_CARD]: ModalAddCard,
  [BRAIN_DELETE]: ModalBrainDelete,
  [DESK_CREATE]: ModalDeskCreation,
  [BUNDLE_CREATE]: ModalBundleCreation,
  [BUNDLE_DELETE]: ModalBundleDelete,
  [VERSION_DELETE]: ModalVersionDelete,
  [MODAL_UPGRADE_PLAN]: ModalUpgradePlan,
  [MODAL_CONFIRMATION]: ModalConfirmation,
  [MODAL_FORM]: ModalForm,
  [MODAL_DELETE]: ModalDelete,
  [MODAL_EDIT]: ModalEdit,
  [MODAL_EDIT_MULTIPLE]: ModalEditMultipleValues,
  [MODAL_IMAGE_CROP]: ModalImageCrop,
  [MODAL_MEMBER_INVITE]: ModalMemberInvite,
  [MODAL_TRYIT]: ModalTryIt,
  [MODAL_PROMPT]: ModalPrompt,
  [MODAL_CONFIRM_CHANGES]: ModalConfirmChanges,
  [MODAL_CANNED_RESPONSES]: ModalCannedResponses,
  [MODAL_ROLE_EDIT]: ModalRoleEdit,
  [MODAL_IMPORT]: ModalImport,
  [MODAL_TEAM]: ModalTeam,
  [MODAL_TEAM_MEMBERS]: ModalTeamMembers,
  [MODAL_MERGE_CONFLICTS]: ModalMergeConflicts,
  [MODAL_CONNECT_INTENT]: ModalConnectIntent,
  [MODAL_AUTO_BRAIN]: ModalAutoBrain,
  [MODAL_WARN]: ModalWarn,
  [MODAL_WHATSAPP_TEST]: ModalWhatsAppTest,
  [MODAL_DEPARTMENT_CREATE]: ModalDepartmentCreate,
  [MODAL_ACCOUNT_TRANSFER]: ModalAccountTransfer,
  [MODAL_PUBLISH_VERSION]: ModalPublishVersion,
  [MODAL_MEDIA_PREVIEW]: ModalMediaPreview,
  [MODAL_AI_AGENT_CREATE]: ModalAIAgentCreate,
  [MODAL_TEMPLATE_MESSAGE]: ModalTemplateMessage,
  [MODAL_UNSAVED_CHANGES]: ModalUnsavedChanges,
  [MODAL_VIEWS]: ModalViews,
  [MODAL_DIALOG_PREVIEW]: ModalPreviewDialog,
  [MODAL_CREATE_SAMPLE_DIALOG]: ModalCreateSampleDialog,
  [MODAL_DOCUMENT_PREVIEW]: ModalDocumentPreview,
  [MODAL_NEW_MESSAGE]: NewMessageModal,
  [MODAL_INTEGRATION_API_KEY_CREATED]: ModalIntegrationApiKeyCreated,
  [MODAL_REUSE_TIMER]: ReuseTimerNodeDialog as React.ComponentType,
};

const ModalConductor = () => {
  const modals = useSelector((state: RootState) => state.modals);
  if (!modals || modals.length === 0) {
    return null;
  }

  const { kind, props } = modals[modals.length - 1];

  const Component = MODAL_KINDS[kind];
  if (!Component) {
    return null;
  }
  return <Component {...props} />;
};

export default ModalConductor;
